import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step", "stepIndicator", "prevBtn", "nextBtn", "submitBtn",
    "phaseField", "timezoneSection", "contractorSection", "contractorToggle",
    "addressField", "cityField", "stateField", "zipcodeField",
    "latitudeField", "longitudeField",
    "tagNameField", "projectName", "projectNameField",
    "portfolioSelect", "programSelect",
    "projectNumberField",
    "startDate", "endDate", "contractDays",
    "dateLabel", "dateRequired",
    "currencySection",
    "reviewContent",
    "form",
    // Bidding targets
    "datesSection", "preBidSection", "bidOpeningSection",
    "preBidFields", "preBidExtras", "bidOpeningFields",
    "preBidTimezoneCol", "preBidTzSelect",
    "preBidLocationLink", "preBidLocationFields",
    "bidOpeningTzCol", "bidOpeningTzSelect",
    "unitPricingList", "bidAlternatesList", "liquidationAmount",
    "timezoneLabel", "contractorLabel",
    "emailNotifTiming", "eftSection",
    "newAdminAssistantField",
    "ccLast4", "ccBrand", "ccChargeId",
    "newCardSection", "existingCardId", "paymentMethodSelect", "paymentFrame",
    "phaseSelector",
    // Real Estate targets
    "propertySearchSection", "realEstateLocationSection",
    "constructionDetailsSection", "realEstateDetailsSection",
    "constructionTeamSection", "realEstateTeamSection"
  ]

  static values = {
    currentStep: { type: Number, default: 0 },
    totalSteps: { type: Number, default: 5 },
    phase: String,
    // Pre-construction (2026-10-20): a builder's own job in the bidding
    // phase, with no bid settings, no payment and no bidding contractors.
    precon: { type: Boolean, default: false },
    checkCodeUrl: String,
    programsUrlTemplate: String,
    companyId: Number
  }

  connect() {
    this._maxStepReached = 0
    this._computeActiveSteps()
    this.showStep(0)
    this.applyPhaseRules()
    this._cacheCardTemplates()
  }

  _cacheCardTemplates() {
    this._cardTemplates = {}
    this.element.querySelectorAll("[data-team-cards]").forEach(container => {
      const role = container.dataset.teamCards
      const card = container.querySelector("[data-controller='team-card']")
      if (card) this._cardTemplates[role] = card.cloneNode(true)
    })
  }

  // Owner-side bidding rules apply to a bidding project, except a
  // pre-construction job, which follows the construction steps.
  get _isBidding() {
    return this.phaseValue === "bidding" && !this.preconValue
  }

  // ── Step Index Mapping ─────────────────────────────────────────────
  // Physical steps: 0=Location, 1=Details, 2=BidSettings, 3=Team, 4=Review
  // For non-bidding: skip physical index 2 (Bid Settings)

  _computeActiveSteps() {
    const isBidding = this._isBidding
    if (isBidding) {
      this._activeSteps = [0, 1, 2, 3, 4]
    } else {
      this._activeSteps = [0, 1, 3, 4] // skip bid settings (physical 2)
    }
  }

  _logicalToPhysical(logicalIndex) {
    return this._activeSteps[logicalIndex]
  }

  _physicalToLogical(physicalIndex) {
    return this._activeSteps.indexOf(physicalIndex)
  }

  get _logicalStepCount() {
    return this._activeSteps.length
  }

  get _currentLogical() {
    return this._physicalToLogical(this.currentStepValue)
  }

  // ── Step Navigation ──────────────────────────────────────────────────

  nextStep() {
    const logical = this._currentLogical
    if (!this.validateCurrentStep()) return
    if (logical < this._logicalStepCount - 1) {
      const nextLogical = logical + 1
      this.currentStepValue = this._logicalToPhysical(nextLogical)
      if (nextLogical > this._maxStepReached) {
        this._maxStepReached = nextLogical
      }
      this.showStep(this.currentStepValue)
      if (nextLogical === this._logicalStepCount - 1) {
        this.buildReview()
      }
    }
  }

  prevStep() {
    const logical = this._currentLogical
    if (logical > 0) {
      this.currentStepValue = this._logicalToPhysical(logical - 1)
      this.showStep(this.currentStepValue)
    }
  }

  goToStep(event) {
    const physicalTarget = parseInt(event.currentTarget.dataset.step, 10)
    if (physicalTarget === this.currentStepValue) return
    const logicalTarget = this._physicalToLogical(physicalTarget)
    if (logicalTarget === -1) return // step not active
    if (logicalTarget <= this._maxStepReached) {
      this.currentStepValue = physicalTarget
      this.showStep(this.currentStepValue)
      if (logicalTarget === this._logicalStepCount - 1) {
        this.buildReview()
      }
    }
  }

  showStep(physicalIndex) {
    this.stepTargets.forEach((el, i) => {
      el.classList.toggle("hidden", i !== physicalIndex)
    })

    // Lazy-load payment card turbo frame when bid settings step (physical 2) is shown
    if (physicalIndex === 2 && this.hasPaymentFrameTarget) {
      const frame = this.paymentFrameTarget
      if (!frame.src && frame.dataset.src) {
        frame.src = frame.dataset.src
      }
    }

    // Phase selector: only visible on step 1 (physical 0), and not for real_estate
    if (this.hasPhaseSelectorTarget) {
      const showPhaseSelector = physicalIndex === 0 && this.phaseValue !== "real_estate" && !this.preconValue
      this.phaseSelectorTarget.classList.toggle("hidden", !showPhaseSelector)
    }

    const logicalIndex = this._physicalToLogical(physicalIndex)

    // Update step indicators - only visible ones
    this.stepIndicatorTargets.forEach((indicator, i) => {
      const isBiddingOnly = indicator.dataset.biddingOnly === "true"
      const isBidding = this._isBidding

      // Show/hide bidding-only indicators
      if (isBiddingOnly) {
        indicator.classList.toggle("hidden", !isBidding)
      }

      // Only style active indicators
      const logicalI = this._physicalToLogical(i)
      if (logicalI === -1) return // skip inactive steps

      const circle = indicator.querySelector("[data-circle]")
      const label = indicator.querySelector("[data-label]")
      const line = indicator.querySelector("[data-line]")
      const btn = indicator.querySelector("button")
      const stepNum = indicator.querySelector("[data-step-num]")

      // Renumber visible steps
      if (stepNum) stepNum.textContent = logicalI + 1

      if (logicalI === logicalIndex) {
        // Active
        circle?.classList.remove("border-gray-300", "text-gray-400", "bg-brand", "text-white")
        circle?.classList.add("border-brand", "text-brand", "bg-brand/10")
        label?.classList.remove("text-gray-400", "text-brand")
        label?.classList.add("text-brand", "font-semibold")
      } else if (logicalI <= this._maxStepReached) {
        // Completed / visited
        circle?.classList.remove("border-gray-300", "text-gray-400", "border-brand", "text-brand", "bg-brand/10")
        circle?.classList.add("border-brand", "bg-brand", "text-white")
        label?.classList.remove("text-gray-400", "text-brand", "font-semibold")
        label?.classList.add("text-brand")
      } else {
        // Upcoming / not yet reached
        circle?.classList.remove("border-brand", "text-brand", "bg-brand", "text-white", "bg-brand/10")
        circle?.classList.add("border-gray-300", "text-gray-400")
        label?.classList.remove("text-brand", "font-semibold")
        label?.classList.add("text-gray-400")
      }

      // Connector line
      if (line) {
        if (logicalI < this._maxStepReached) {
          line.classList.remove("bg-gray-200")
          line.classList.add("bg-brand")
        } else {
          line.classList.remove("bg-brand")
          line.classList.add("bg-gray-200")
        }
      }

      // Cursor
      if (btn) {
        btn.classList.toggle("cursor-pointer", logicalI <= this._maxStepReached)
        btn.classList.toggle("cursor-default", logicalI > this._maxStepReached)
      }
    })

    // Button visibility
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.classList.toggle("invisible", logicalIndex === 0)
    }
    if (this.hasNextBtnTarget) {
      const isLast = logicalIndex === this._logicalStepCount - 1
      this.nextBtnTarget.classList.toggle("hidden", isLast)
      this.nextBtnTarget.classList.toggle("inline-flex", !isLast)
    }
    if (this.hasSubmitBtnTarget) {
      const isLastStep = logicalIndex === this._logicalStepCount - 1
      this.submitBtnTarget.classList.toggle("hidden", !isLastStep)
      this.submitBtnTarget.classList.toggle("inline-flex", isLastStep)
    }
  }

  // ── Phase Rules ──────────────────────────────────────────────────────

  phaseChanged(event) {
    this.phaseValue = event.currentTarget.value || event.currentTarget.dataset.phase
    if (this.hasPhaseFieldTarget) {
      this.phaseFieldTarget.value = this.phaseValue
    }

    // Update phase card visuals
    this._updatePhaseCards()

    // Recompute step mapping and reset navigation
    const oldLogical = this._currentLogical
    this._computeActiveSteps()
    if (this._maxStepReached > this._logicalStepCount - 1) {
      this._maxStepReached = this._logicalStepCount - 1
    }
    // Stay on current logical step if possible, else go to 0
    const newPhysical = this._logicalToPhysical(Math.min(oldLogical, this._logicalStepCount - 1))
    this.currentStepValue = newPhysical !== undefined ? newPhysical : 0
    this.showStep(this.currentStepValue)

    this.applyPhaseRules()
  }

  _updatePhaseCards() {
    const cards = this.element.querySelectorAll("input[name='phase_selector']")
    cards.forEach(radio => {
      const label = radio.closest("label")
      if (!label) return
      const isSelected = radio.value === this.phaseValue
      const iconEl = label.querySelector("svg, [class*='heroicon'], img")
      const dot = label.querySelector(".rounded-full.border-2")

      // Card border/bg
      if (isSelected) {
        label.classList.remove("border-gray-200", "bg-white", "hover:border-gray-300")
        label.classList.add("border-brand", "bg-brand/5", "ring-1", "ring-brand")
      } else {
        label.classList.remove("border-brand", "bg-brand/5", "ring-1", "ring-brand")
        label.classList.add("border-gray-200", "bg-white", "hover:border-gray-300")
      }

      // Icon color
      const iconWrapper = label.querySelector(".flex.items-center.gap-2 svg, .flex.items-center.gap-2 img")
      if (iconWrapper) {
        iconWrapper.classList.toggle("text-brand", isSelected)
        iconWrapper.classList.toggle("text-gray-400", !isSelected)
      }

      // Radio dot
      if (dot) {
        if (isSelected) {
          dot.classList.remove("border-gray-300")
          dot.classList.add("border-brand", "bg-brand")
          if (!dot.querySelector("svg")) {
            dot.innerHTML = '<svg class="w-full h-full text-white" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="8" r="3"/></svg>'
          }
        } else {
          dot.classList.remove("border-brand", "bg-brand")
          dot.classList.add("border-gray-300")
          dot.innerHTML = ""
        }
      }
    })
  }

  applyPhaseRules() {
    const isDev = this.phaseValue === "dev"
    const isBidding = this._isBidding
    const isRealEstate = this.phaseValue === "real_estate"

    // ── Location step toggles ──
    if (this.hasPropertySearchSectionTarget) {
      this.propertySearchSectionTarget.classList.toggle("hidden", isRealEstate)
    }
    if (this.hasRealEstateLocationSectionTarget) {
      this.realEstateLocationSectionTarget.classList.toggle("hidden", !isRealEstate)
    }

    // Timezone: visible for construction only — bidding uses bid opening timezone in Details step
    if (this.hasTimezoneSectionTarget) {
      this.timezoneSectionTarget.classList.toggle("hidden", isDev || isRealEstate || isBidding)
    }

    // ── Details step toggles ──
    if (this.hasConstructionDetailsSectionTarget) {
      this.constructionDetailsSectionTarget.classList.toggle("hidden", isRealEstate)
    }
    if (this.hasRealEstateDetailsSectionTarget) {
      this.realEstateDetailsSectionTarget.classList.toggle("hidden", !isRealEstate)
    }

    // Date labels
    this.dateLabelTargets.forEach(el => {
      el.textContent = isDev ? el.dataset.devLabel : el.dataset.constructionLabel
    })

    // Date required indicators
    this.dateRequiredTargets.forEach(el => {
      el.classList.toggle("hidden", isDev || isBidding || isRealEstate)
    })

    // Dates section: hidden for bidding (and RE uses its own section)
    if (this.hasDatesSectionTarget) {
      this.datesSectionTarget.classList.toggle("hidden", isBidding)
    }

    // Pre-bid and bid opening sections: shown for bidding
    if (this.hasPreBidSectionTarget) {
      this.preBidSectionTarget.classList.toggle("hidden", !isBidding)
    }
    if (this.hasBidOpeningSectionTarget) {
      this.bidOpeningSectionTarget.classList.toggle("hidden", !isBidding)
    }

    // ── Team step toggles ──
    if (this.hasConstructionTeamSectionTarget) {
      this.constructionTeamSectionTarget.classList.toggle("hidden", isRealEstate)
    }
    if (this.hasRealEstateTeamSectionTarget) {
      this.realEstateTeamSectionTarget.classList.toggle("hidden", !isRealEstate)
    }

    // Contractor section toggle for dev
    if (this.hasContractorSectionTarget && this.hasContractorToggleTarget) {
      this.contractorToggleTarget.classList.toggle("hidden", !isDev)
      if (!isDev) {
        this.contractorSectionTarget.classList.remove("hidden")
      }
    }

    // Contractor label for bidding
    if (this.hasContractorLabelTarget) {
      this.contractorLabelTarget.textContent = isBidding ? "Bidding Contractors" : "Contractor Companies"
    }
  }

  toggleContractors(event) {
    if (this.hasContractorSectionTarget) {
      this.contractorSectionTarget.classList.toggle("hidden", !event.currentTarget.checked)
    }
  }

  // ── Bidding Event Handlers ─────────────────────────────────────────

  preBidScheduleChanged(event) {
    const show = event.currentTarget.value !== "no"
    if (this.hasPreBidFieldsTarget) {
      this.preBidFieldsTarget.classList.toggle("hidden", !show)
    }
    if (this.hasPreBidTimezoneColTarget) {
      this.preBidTimezoneColTarget.classList.toggle("hidden", !show)
    }
    if (this.hasPreBidExtrasTarget) {
      this.preBidExtrasTarget.classList.toggle("hidden", !show)
    }
    // Show location link for onsite/onsite_virtual (fields stay hidden until user clicks the link)
    const showLocation = event.currentTarget.value === "onsite" || event.currentTarget.value === "onsite_virtual"
    const fieldsExpanded = this.hasPreBidLocationFieldsTarget && !this.preBidLocationFieldsTarget.classList.contains("hidden")
    if (this.hasPreBidLocationLinkTarget) {
      this.preBidLocationLinkTarget.classList.toggle("hidden", !showLocation || fieldsExpanded)
    }
    // Hide location fields when switching away from onsite/onsite_virtual
    if (!showLocation && this.hasPreBidLocationFieldsTarget) {
      this.preBidLocationFieldsTarget.classList.add("hidden")
    }
  }

  togglePreBidLocation(event) {
    event.preventDefault()
    if (this.hasPreBidLocationLinkTarget) {
      this.preBidLocationLinkTarget.classList.add("hidden")
    }
    if (this.hasPreBidLocationFieldsTarget) {
      this.preBidLocationFieldsTarget.classList.remove("hidden")
    }
  }

  clearPreBidLocation(event) {
    event.preventDefault()
    if (this.hasPreBidLocationFieldsTarget) {
      this.preBidLocationFieldsTarget.querySelectorAll("input").forEach(i => i.value = "")
      this.preBidLocationFieldsTarget.classList.add("hidden")
    }
    if (this.hasPreBidLocationLinkTarget) {
      this.preBidLocationLinkTarget.classList.remove("hidden")
    }
  }

  diffPreBidTimezoneChanged(event) {
    // When unchecked, sync pre-bid timezone to match bid opening
    if (!event.currentTarget.checked && this.hasPreBidTzSelectTarget && this.hasBidOpeningTzSelectTarget) {
      this.preBidTzSelectTarget.value = this.bidOpeningTzSelectTarget.value
    }
  }

  bidTimezoneChanged(event) {
    const diffCheckbox = this.element.querySelector('input[name="project[diff_pre_bid_timezone]"]')
    if (diffCheckbox && diffCheckbox.checked) return

    // Keep both selects in sync
    if (this.hasPreBidTzSelectTarget && this.hasBidOpeningTzSelectTarget) {
      const other = event.currentTarget === this.preBidTzSelectTarget
        ? this.bidOpeningTzSelectTarget
        : this.preBidTzSelectTarget
      other.value = event.currentTarget.value
    }
  }

  bidMeetingScheduleChanged(event) {
    const show = event.currentTarget.value !== "tbd"
    if (this.hasBidOpeningFieldsTarget) {
      this.bidOpeningFieldsTarget.classList.toggle("hidden", !show)
    }
    if (this.hasBidOpeningTzColTarget) {
      this.bidOpeningTzColTarget.classList.toggle("hidden", !show)
    }
  }

  unitPricingChanged(event) {
    if (this.hasUnitPricingListTarget) {
      this.unitPricingListTarget.classList.toggle("hidden", event.currentTarget.value !== "yes_list")
    }
  }

  bidAlternatesChanged(event) {
    if (this.hasBidAlternatesListTarget) {
      this.bidAlternatesListTarget.classList.toggle("hidden", event.currentTarget.value !== "yes_list")
    }
  }

  liquidationChanged(event) {
    if (this.hasLiquidationAmountTarget) {
      this.liquidationAmountTarget.classList.toggle("hidden", event.currentTarget.value !== "1")
    }
  }

  liquidationToggled(event) {
    if (this.hasLiquidationAmountTarget) {
      this.liquidationAmountTarget.classList.toggle("hidden", !event.currentTarget.checked)
    }
  }

  emailNotifChanged(event) {
    if (this.hasEmailNotifTimingTarget) {
      this.emailNotifTimingTarget.classList.toggle("hidden", !event.currentTarget.checked)
    }
  }

  paymentMethodChanged(event) {
    const val = event.currentTarget.value
    if (this.hasEftSectionTarget) {
      this.eftSectionTarget.classList.toggle("hidden", val !== "eft")
    }
    if (this.hasNewCardSectionTarget) {
      this.newCardSectionTarget.classList.toggle("hidden", val !== "new_cc")
      if (val === "new_cc") this._initStripeElements()
    }
  }

  _initStripeElements() {
    if (this._stripeCardElement) return
    const keyEl = this.element.querySelector("[data-stripe-key]")
    if (!keyEl) return
    const key = keyEl.dataset.stripeKey
    if (!key || typeof Stripe === "undefined") return

    this._stripe = Stripe(key)
    const elements = this._stripe.elements()
    const cs = getComputedStyle(document.documentElement)
    this._stripeCardElement = elements.create("card", {
      style: {
        base: { fontSize: "14px", color: cs.getPropertyValue("--color-text-heading").trim() || "#1f2937", "::placeholder": { color: cs.getPropertyValue("--color-text-muted").trim() || "#9ca3af" } },
        invalid: { color: cs.getPropertyValue("--color-danger").trim() || "#dc2626" }
      }
    })
    this._stripeCardElement.mount("#card-element")
    this._stripeCardElement.on("change", (event) => {
      const errEl = document.getElementById("card-errors")
      if (errEl) errEl.textContent = event.error ? event.error.message : ""
    })
  }

  // ── Tag Name Selected ────────────────────────────────────────────────

  tagNameSelected(event) {
    const { id, name, address, city, state, zipcode, country, latitude, longitude, region_id } = event.detail

    if (this.hasAddressFieldTarget) this.addressFieldTarget.value = address || ""
    if (this.hasCityFieldTarget) this.cityFieldTarget.value = city || ""
    if (this.hasStateFieldTarget) this.stateFieldTarget.value = state || ""
    if (this.hasZipcodeFieldTarget) this.zipcodeFieldTarget.value = zipcode || ""
    if (this.hasLatitudeFieldTarget) this.latitudeFieldTarget.value = latitude || ""
    if (this.hasLongitudeFieldTarget) this.longitudeFieldTarget.value = longitude || ""
    if (this.hasProjectNameFieldTarget) this.projectNameFieldTarget.value = name || ""

    this._autoSelectTimezone(state, country)
    this._dispatchRegionChanged(region_id)
    this._clearAllErrors()
  }

  // ── Property Created (from Add Property modal) ─────────────────────

  propertyCreated(event) {
    if (!event.detail) return
    const tagName = event.detail

    if (this.hasTagNameFieldTarget) this.tagNameFieldTarget.value = tagName.id

    const searchInput = this.element.querySelector("[data-autocomplete-target='input'][data-tag-search]")
    if (searchInput) searchInput.value = `${tagName.code} - ${tagName.name}`

    if (this.hasAddressFieldTarget) this.addressFieldTarget.value = tagName.address || ""
    if (this.hasCityFieldTarget) this.cityFieldTarget.value = tagName.city || ""
    if (this.hasStateFieldTarget) this.stateFieldTarget.value = tagName.state || ""
    if (this.hasZipcodeFieldTarget) this.zipcodeFieldTarget.value = tagName.zipcode || ""
    if (this.hasLatitudeFieldTarget) this.latitudeFieldTarget.value = tagName.latitude || ""
    if (this.hasLongitudeFieldTarget) this.longitudeFieldTarget.value = tagName.longitude || ""
    if (this.hasProjectNameFieldTarget) this.projectNameFieldTarget.value = `${tagName.code} - ${tagName.name}`

    this._autoSelectTimezone(tagName.state, tagName.country)
    this._dispatchRegionChanged(tagName.region_id)
    this._clearAllErrors()
  }

  _dispatchRegionChanged(regionId) {
    document.dispatchEvent(new CustomEvent("owner-members:region-changed", {
      detail: { regionId: regionId || null }
    }))
  }

  // ── Admin Assistant Selected ─────────────────────────────────────────

  adminAssistantChanged(event) {
    const select = event.currentTarget
    const email = select.value || null
    // Option text is "Name - email" or just email — strip the trailing " - email"
    // so the chip shows only the human-readable name when available.
    const rawLabel = email ? select.options[select.selectedIndex]?.text : null
    const nameOnly = rawLabel ? rawLabel.replace(/\s*-\s*\S+@\S+\s*$/, "").trim() : null
    const label = nameOnly || email
    document.dispatchEvent(new CustomEvent("owner-members:aa-changed", {
      detail: { email, label }
    }))
  }

  _autoSelectTimezone(state, country) {
    const tz = this._inferTimezone(state, country)
    if (!tz) return

    // For bidding, set the bid opening timezone (which IS the project timezone)
    // and sync to pre-bid timezone if not explicitly different.
    // For other phases, set the Step 1 timezone select.
    if (this._isBidding) {
      if (this.hasBidOpeningTzSelectTarget) {
        for (const opt of this.bidOpeningTzSelectTarget.options) {
          if (opt.value === tz) { this.bidOpeningTzSelectTarget.value = tz; break }
        }
      }
      const diffCheckbox = this.element.querySelector('input[name="project[diff_pre_bid_timezone]"]')
      if (this.hasPreBidTzSelectTarget && (!diffCheckbox || !diffCheckbox.checked)) {
        for (const opt of this.preBidTzSelectTarget.options) {
          if (opt.value === tz) { this.preBidTzSelectTarget.value = tz; break }
        }
      }
    } else {
      const select = this.element.querySelector("[name='project[time_zone]']")
      if (!select) return
      for (const opt of select.options) {
        if (opt.value === tz) { select.value = tz; return }
      }
    }
  }

  _inferTimezone(state, country) {
    if (state) {
      const usMatch = this._usStateTimezone(state.toUpperCase())
      if (usMatch) return usMatch
    }
    if (country) {
      const countryMatch = this._countryTimezone(country.toUpperCase())
      if (countryMatch) return countryMatch
    }
    return null
  }

  _usStateTimezone(state) {
    const E = "Eastern Time (US & Canada)"
    const C = "Central Time (US & Canada)"
    const M = "Mountain Time (US & Canada)"
    const P = "Pacific Time (US & Canada)"
    const map = {
      CT: E, DE: E, FL: E, GA: E, IN: E, KY: E, MA: E, MD: E, ME: E,
      MI: E, NC: E, NH: E, NJ: E, NY: E, OH: E, PA: E, RI: E, SC: E,
      VA: E, VT: E, WV: E, DC: E,
      AL: C, AR: C, IA: C, IL: C, KS: C, LA: C, MN: C, MO: C, MS: C,
      NE: C, ND: C, OK: C, SD: C, TN: C, TX: C, WI: C,
      CO: M, ID: M, MT: M, NM: M, UT: M, WY: M,
      AZ: "Arizona",
      CA: P, NV: P, OR: P, WA: P,
      AK: "Alaska", HI: "Hawaii"
    }
    // Also accept full state names
    const nameToAbbr = {
      CONNECTICUT: "CT", DELAWARE: "DE", FLORIDA: "FL", GEORGIA: "GA",
      INDIANA: "IN", KENTUCKY: "KY", MASSACHUSETTS: "MA", MARYLAND: "MD",
      MAINE: "ME", MICHIGAN: "MI", "NORTH CAROLINA": "NC",
      "NEW HAMPSHIRE": "NH", "NEW JERSEY": "NJ", "NEW YORK": "NY",
      OHIO: "OH", PENNSYLVANIA: "PA", "RHODE ISLAND": "RI",
      "SOUTH CAROLINA": "SC", VIRGINIA: "VA", VERMONT: "VT",
      "WEST VIRGINIA": "WV", "DISTRICT OF COLUMBIA": "DC",
      ALABAMA: "AL", ARKANSAS: "AR", IOWA: "IA", ILLINOIS: "IL",
      KANSAS: "KS", LOUISIANA: "LA", MINNESOTA: "MN", MISSOURI: "MO",
      MISSISSIPPI: "MS", NEBRASKA: "NE", "NORTH DAKOTA": "ND",
      OKLAHOMA: "OK", "SOUTH DAKOTA": "SD", TENNESSEE: "TN",
      TEXAS: "TX", WISCONSIN: "WI",
      COLORADO: "CO", IDAHO: "ID", MONTANA: "MT", "NEW MEXICO": "NM",
      UTAH: "UT", WYOMING: "WY",
      ARIZONA: "AZ",
      CALIFORNIA: "CA", NEVADA: "NV", OREGON: "OR", WASHINGTON: "WA",
      ALASKA: "AK", HAWAII: "HI"
    }
    const abbr = nameToAbbr[state] || state
    return map[abbr] || null
  }

  _countryTimezone(country) {
    const map = {
      BR: "Brasilia", BRAZIL: "Brasilia",
      PT: "Lisbon", PORTUGAL: "Lisbon",
      ES: "Madrid", SPAIN: "Madrid",
      FR: "Paris", FRANCE: "Paris",
      DE: "Berlin", GERMANY: "Berlin",
      IT: "Rome", ITALY: "Rome",
      GB: "London", UK: "London", "UNITED KINGDOM": "London",
      JP: "Tokyo", JAPAN: "Tokyo",
      AU: "Sydney", AUSTRALIA: "Sydney",
      CA: "Eastern Time (US & Canada)", CANADA: "Eastern Time (US & Canada)",
      MX: "Mexico City", MEXICO: "Mexico City",
      CL: "Santiago", CHILE: "Santiago",
      AR: "Buenos Aires", ARGENTINA: "Buenos Aires",
      CO: "Bogota", COLOMBIA: "Bogota",
      PE: "Lima", PERU: "Lima",
      SA: "Riyadh", "SAUDI ARABIA": "Riyadh",
      AE: "Abu Dhabi", UAE: "Abu Dhabi",
      IN: "Mumbai", INDIA: "Mumbai",
      CN: "Beijing", CHINA: "Beijing",
      KR: "Seoul", "SOUTH KOREA": "Seoul"
    }
    return map[country] || null
  }

  async checkDuplicateCode() {
    if (!this.checkCodeUrlValue || !this.hasProjectNumberFieldTarget) return
    const code = this.projectNumberFieldTarget.value.trim()
    if (!code) return

    try {
      const url = `${this.checkCodeUrlValue}?code=${encodeURIComponent(code)}`
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!resp.ok) return
      const data = await resp.json()
      const warning = this.projectNumberFieldTarget.parentElement?.querySelector("[data-duplicate-warning]")
      if (warning) {
        warning.classList.toggle("hidden", !data.exists)
      }
    } catch { /* ignore */ }
  }

  // ── Portfolio / Program ──────────────────────────────────────────────

  async portfolioChanged() {
    if (!this.hasProgramSelectTarget || !this.hasPortfolioSelectTarget) return
    const portfolioId = this.portfolioSelectTarget.value
    this.programSelectTarget.innerHTML = '<option value="">Select...</option>'

    if (!portfolioId || !this.programsUrlTemplateValue) return

    try {
      const url = this.programsUrlTemplateValue.replace("__ID__", portfolioId)
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!resp.ok) return
      const programs = await resp.json()
      programs.forEach(p => {
        const opt = document.createElement("option")
        opt.value = p.id
        opt.textContent = p.name
        this.programSelectTarget.appendChild(opt)
      })
    } catch { /* ignore */ }
  }

  // ── Date / Contract Days Autocalculation ─────────────────────────────

  startDateChanged() {
    if (!this.hasStartDateTarget || !this.hasEndDateTarget || !this.hasContractDaysTarget) return
    const start = this.startDateTarget.value
    const end = this.endDateTarget.value
    const days = parseInt(this.contractDaysTarget.value, 10)

    if (start && days > 0) {
      this._setEndDateFromDays(start, days)
    } else if (start && end) {
      this._setContractDaysFromDates(start, end)
    }
  }

  endDateChanged() {
    if (!this.hasStartDateTarget || !this.hasEndDateTarget || !this.hasContractDaysTarget) return
    const start = this.startDateTarget.value
    const end = this.endDateTarget.value
    const days = parseInt(this.contractDaysTarget.value, 10)

    if (start && end) {
      this._setContractDaysFromDates(start, end)
    } else if (end && days > 0 && !start) {
      this._setStartDateFromDays(end, days)
    }
  }

  contractDaysChanged() {
    if (!this.hasStartDateTarget || !this.hasEndDateTarget || !this.hasContractDaysTarget) return
    const start = this.startDateTarget.value
    const days = parseInt(this.contractDaysTarget.value, 10)

    if (!days || days <= 0) {
      this._setFlatpickrDate(this.endDateTarget, "")
      return
    }

    if (start) {
      this._setEndDateFromDays(start, days)
    } else if (end) {
      this._setStartDateFromDays(end, days)
    }
  }

  _setContractDaysFromDates(startStr, endStr) {
    let diff = Math.round((this._parseDate(endStr) - this._parseDate(startStr)) / (1000 * 60 * 60 * 24))
    if (this.companyIdValue === 549) diff += 1
    this.contractDaysTarget.value = diff > 0 ? diff : ""
  }

  _setEndDateFromDays(startStr, days) {
    const date = this._parseDate(startStr)
    const offset = this.companyIdValue === 549 ? days - 1 : days
    date.setDate(date.getDate() + offset)
    this._setFlatpickrDate(this.endDateTarget, date)
  }

  _setStartDateFromDays(endStr, days) {
    const date = this._parseDate(endStr)
    const offset = this.companyIdValue === 549 ? days - 1 : days
    date.setDate(date.getDate() - offset)
    this._setFlatpickrDate(this.startDateTarget, date)
  }

  // new Date("Y-m-d") parses as UTC midnight; local getDate/setDate math on it
  // lands a day early anywhere west of UTC. Parse into local date parts instead.
  _parseDate(str) {
    const [y, m, d] = str.split("-").map(Number)
    return new Date(y, m - 1, d)
  }

  _setFlatpickrDate(input, date) {
    const fp = input._flatpickr
    if (fp) {
      // Never trigger change here: it would re-enter our own handlers and
      // overwrite the field the user just typed.
      fp.setDate(date, false)
    } else {
      input.value = date ? this._formatDate(date) : ""
    }
  }

  _formatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  // ── Validation ───────────────────────────────────────────────────────

  validateCurrentStep() {
    return this._validateStep(this.currentStepValue)
  }

  // Owner's Reps (Phelipe 2026-10-28): another owner means other regions,
  // portfolios and people, so the form reloads for it.
  ownerChanged(event) {
    const url = event.target.selectedOptions[0]?.dataset.url
    if (url) window.location.assign(url)
  }

  _validateStep(physicalIndex) {
    const stepEl = this.stepTargets[physicalIndex]
    if (!stepEl) return true

    let valid = true
    const requiredFields = stepEl.querySelectorAll("[data-required]")
    const isBidding = this._isBidding
    const isDev = this.phaseValue === "dev"
    const isRealEstate = this.phaseValue === "real_estate"

    requiredFields.forEach(field => {
      // Skip fields inside a hidden parent section
      if (field.closest(".hidden")) {
        this.clearFieldError(field)
        return
      }

      // Skip hidden bidding fields
      if (isBidding && field.dataset.biddingHidden === "true") {
        this.clearFieldError(field)
        return
      }

      if (isDev && field.dataset.devOptional === "true") {
        this.clearFieldError(field)
        return
      }

      // Skip construction-only fields when RE phase
      if (isRealEstate && field.dataset.reHidden === "true") {
        this.clearFieldError(field)
        return
      }

      // Skip RE-only fields when not RE phase
      if (!isRealEstate && field.dataset.reOnly === "true") {
        this.clearFieldError(field)
        return
      }

      if (!field.value || !field.value.trim()) {
        this.showFieldError(field)
        valid = false
      } else {
        this.clearFieldError(field)
      }
    })

    return valid
  }

  _validateTeamDuplicateCompanies() {
    const containers = this.element.querySelectorAll("[data-team-cards]")
    const seen = new Map()
    const roleLabels = {
      consultants: "Consultants",
      contractors: "Contractors",
      other_vendors: "Other Vendors",
      support: "Stakeholders",
      owner_reps: "Owner's Reps",
      outsource_partners: "Outsource Partners",
      re_consultants: "Principal Consultant"
    }

    for (const container of containers) {
      const role = container.dataset.teamCards
      const label = roleLabels[role] || role
      const cards = container.querySelectorAll("[data-controller*='team-card']")

      for (const card of cards) {
        const hidden = card.querySelector("[data-team-card-target='companyHidden']")
        const companyId = hidden?.value
        if (!companyId) continue

        // Also under Owner's Reps: it becomes the Owner's Rep and its people
        // join that card on save (Phelipe 2026-10-21) — not a clash.
        if (seen.has(companyId) && (role === "owner_reps" || seen.get(companyId) === roleLabels.owner_reps)) continue
        if (seen.has(companyId)) {
          const input = card.querySelector("[data-team-card-target='companyInput']")
          const companyName = input?.tagName === "SELECT"
            ? (input.options[input.selectedIndex]?.text || `Company #${companyId}`)
            : (input?.value || `Company #${companyId}`)
          return `"${companyName}" is already assigned as ${seen.get(companyId)}. Each company can only have one role per project.`
        }
        seen.set(companyId, label)
      }
    }

    return null
  }

  async submitProject() {
    // Validate all active steps except the review step
    for (let logical = 0; logical < this._logicalStepCount - 1; logical++) {
      const physical = this._logicalToPhysical(logical)
      if (!this._validateStep(physical)) {
        this.currentStepValue = physical
        this.showStep(physical)
        return
      }
    }

    // Validate no duplicate companies across team roles
    const teamDuplicateError = this._validateTeamDuplicateCompanies()
    if (teamDuplicateError) {
      const teamStepPhysical = 3
      this.currentStepValue = teamStepPhysical
      this.showStep(teamStepPhysical)
      this._showServerError([teamDuplicateError])
      return
    }

    // All valid — show spinner and submit via fetch
    this._setSubmitLoading(true)
    this._clearServerError()

    const form = this.hasFormTarget ? this.formTarget : this.element.closest("form")
    if (!form) return

    // Handle payment for bidding projects that require CC
    if (this._isBidding && this._requiresPayment()) {
      const paymentMethod = this.hasPaymentMethodSelectTarget ? this.paymentMethodSelectTarget.value : null
      if (paymentMethod === "new_cc") {
        const ok = await this._processNewCardPayment(form)
        if (!ok) { this._setSubmitLoading(false); return }
      } else if (paymentMethod === "existing_card") {
        const ok = await this._processExistingCardPayment(form)
        if (!ok) { this._setSubmitLoading(false); return }
      }
      // eft and free plans just fall through to submit
    }

    try {
      const response = await fetch(form.action, {
        method: form.method || "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrfToken() },
        body: new FormData(form)
      })

      if (response.ok) {
        const data = await response.json()
        window.location.href = data.redirect_to
      } else {
        const data = await response.json().catch(() => null)
        const errors = data?.errors || ["An unexpected error occurred. Please try again."]
        this._showServerError(errors)
        this._setSubmitLoading(false)
      }
    } catch {
      this._showServerError(["A network error occurred. Please try again."])
      this._setSubmitLoading(false)
    }
  }

  _requiresPayment() {
    // If no payment method select exists, the company doesn't need to pay (unlimited/prepaid)
    return this.hasPaymentMethodSelectTarget
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  async _processNewCardPayment(form) {
    if (!this._stripe || !this._stripeCardElement) {
      this._showServerError(["Please enter your credit card details."])
      return false
    }

    const { token, error } = await this._stripe.createToken(this._stripeCardElement)
    if (error) {
      this._showServerError([error.message])
      return false
    }

    const projectNumber = form.querySelector("[name='project[number]']")?.value || ""
    const projectName = form.querySelector("[name='project[name]']")?.value || form.querySelector("[data-autocomplete-target='input'][data-tag-search]")?.value || ""
    const saveCard = form.querySelector("[name='save_card']")?.checked ? "1" : ""

    const body = new FormData()
    body.append("stripe_token", token.id)
    body.append("project_number", projectNumber)
    body.append("project_name", projectName)
    if (saveCard) body.append("save_card", saveCard)

    try {
      const resp = await fetch("/projects/bidding_cc_payment", {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrfToken() },
        body
      })
      const data = await resp.json()
      if (data.error) {
        this._showServerError([data.message || "Payment failed."])
        return false
      }
      if (this.hasCcLast4Target) this.ccLast4Target.value = data.last4
      if (this.hasCcBrandTarget) this.ccBrandTarget.value = data.brand
      if (this.hasCcChargeIdTarget) this.ccChargeIdTarget.value = data.charge_id
      return true
    } catch {
      this._showServerError(["Payment request failed. Please try again."])
      return false
    }
  }

  async _processExistingCardPayment(form) {
    const cardId = this.hasExistingCardIdTarget ? this.existingCardIdTarget.value : ""
    if (!cardId) {
      this._showServerError(["No saved card found."])
      return false
    }

    const projectNumber = form.querySelector("[name='project[number]']")?.value || ""
    const projectName = form.querySelector("[name='project[name]']")?.value || form.querySelector("[data-autocomplete-target='input'][data-tag-search]")?.value || ""

    const body = new FormData()
    body.append("card_id", cardId)
    body.append("project_number", projectNumber)
    body.append("project_name", projectName)

    try {
      const resp = await fetch("/projects/bidding_cc_existing_card_payment", {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrfToken() },
        body
      })
      const data = await resp.json()
      if (data.error) {
        this._showServerError([data.message || "Payment failed."])
        return false
      }
      if (this.hasCcLast4Target) this.ccLast4Target.value = data.last4
      if (this.hasCcBrandTarget) this.ccBrandTarget.value = data.brand
      if (this.hasCcChargeIdTarget) this.ccChargeIdTarget.value = data.charge_id
      return true
    } catch {
      this._showServerError(["Payment request failed. Please try again."])
      return false
    }
  }

  _showServerError(errors) {
    let banner = this.element.querySelector("[data-wizard-error-banner]")
    if (!banner) {
      banner = document.createElement("div")
      banner.setAttribute("data-wizard-error-banner", "")
      banner.className = "p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mt-6"
      const form = this.hasFormTarget ? this.formTarget : this.element.closest("form")
      form.parentElement.appendChild(banner)
    }
    banner.innerHTML = `<p class="font-medium">Please fix the following errors:</p><ul class="list-disc ml-5 mt-1">${errors.map(e => `<li>${this._esc(e)}</li>`).join("")}</ul>`
    banner.classList.remove("hidden")
    banner.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  _clearServerError() {
    const banner = this.element.querySelector("[data-wizard-error-banner]")
    if (banner) banner.classList.add("hidden")
  }

  _setSubmitLoading(loading) {
    const btn = this.submitBtnTarget
    if (loading) {
      btn.dataset.originalWidth = `${btn.offsetWidth}px`
      btn.style.width = btn.dataset.originalWidth
      this._originalSubmitLabel = btn.innerHTML
      btn.disabled = true
      btn.innerHTML = `<svg class="animate-spin h-4 w-4 mx-auto" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
    } else {
      btn.disabled = false
      btn.innerHTML = this._originalSubmitLabel || btn.innerHTML
      btn.style.width = ""
    }
  }

  showFieldError(field) {
    field.classList.add("border-red-400", "ring-1", "ring-red-400")
    const msg = this._findErrorMsg(field)
    if (msg) msg.classList.remove("hidden")

    // Auto-clear on next input/change
    if (!field.dataset.errorListenerAttached) {
      field.dataset.errorListenerAttached = "true"
      const isDatepicker = field.dataset.controller === "datepicker"
      const event = (field.tagName === "SELECT" || isDatepicker) ? "change" : "input"
      field.addEventListener(event, () => this.clearFieldError(field), { once: true })
    }
  }

  clearFieldError(field) {
    field.classList.remove("border-red-400", "ring-1", "ring-red-400")
    const msg = this._findErrorMsg(field)
    if (msg) msg.classList.add("hidden")
    delete field.dataset.errorListenerAttached
  }

  _findErrorMsg(field) {
    return field.parentElement.querySelector("[data-error-msg]")
      || field.parentElement.parentElement.querySelector("[data-error-msg]")
  }

  _clearAllErrors() {
    this.element.querySelectorAll(".border-red-400").forEach(field => {
      this.clearFieldError(field)
    })
  }

  // ── Add Team Card ─────────────────────────────────────────────────────

  addTeamCard(event) {
    const role = event.currentTarget.dataset.role
    const container = this.element.querySelector(`[data-team-cards="${role}"]`)
    if (!container) return

    const template = this._cardTemplates?.[role]
    if (!template) return

    const newCard = template.cloneNode(true)
    newCard.querySelectorAll("input").forEach(input => { input.value = "" })
    const chips = newCard.querySelector("[data-team-card-target='memberChips']")
    if (chips) chips.innerHTML = ""
    const popover = newCard.querySelector("[data-team-card-target='popover']")
    if (popover) popover.classList.add("hidden")
    const popoverList = newCard.querySelector("[data-team-card-target='popoverList']")
    if (popoverList) popoverList.innerHTML = ""
    const addBtn = newCard.querySelector("[data-team-card-target='addMembersBtn']")
    if (addBtn) { addBtn.classList.add("hidden"); addBtn.classList.remove("inline-flex") }

    container.appendChild(newCard)
  }

  // ── Review Step ──────────────────────────────────────────────────────

  buildReview() {
    if (!this.hasReviewContentTarget) return

    const isRealEstate = this.phaseValue === "real_estate"
    if (isRealEstate) {
      this._buildRealEstateReview()
      return
    }

    const getValue = (selector) => {
      const el = this.element.querySelector(selector)
      if (!el) return ""
      if (el.tagName === "SELECT") {
        const val = el.value
        if (!val) return ""
        const text = el.options[el.selectedIndex]?.text || ""
        return text.match(/^select/i) ? "" : text
      }
      return el.value || el.textContent || ""
    }

    // getValue() turns a <select> into its visible label, which is what the
    // summary should print — but anything COMPARED against an option value
    // ("tbd", "1", "yes_list") has to read the value itself. Reading the
    // label here is why the review kept saying "Scheduled" with tomorrow's
    // date after the user picked Date TBD (Maria Bone, 2026-09-04).
    const getRaw = (selector) => this.element.querySelector(selector)?.value || ""

    const isBidding = this._isBidding
    const phase = isBidding ? "Bidding" : (this.phaseValue === "dev" ? "Development" : "Construction")
    const tagDisplay = this.element.querySelector("[data-autocomplete-target='input'][data-tag-search]")?.value || ""
    const address = getValue("[name='project[address]']")
    const city = getValue("[name='project[city]']")
    const state = getValue("[name='project[state]']")
    const zipcode = getValue("[name='project[zipcode]']")
    const timezone = isBidding && this.hasBidOpeningTzSelectTarget
      ? this.bidOpeningTzSelectTarget.value
      : getValue("[name='project[time_zone]']")
    const portfolio = getValue("[name='project[portfolio_id]']")
    const program = getValue("[name='project[program_id]']")
    const kind = getValue("[name='project[kind]']")
    const sqm = getValue("[name='project[square_meters]']")
    const number = getValue("[name='project[number]']")

    const formatDate = (str) => {
      if (!str) return ""
      const d = new Date(str + "T00:00:00")
      if (isNaN(d)) return str
      return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    }

    // Dates (not shown for bidding)
    let dateRows = ""
    if (!isBidding) {
      const startDate = formatDate(getValue("[name='project[started_at]']"))
      const endDate = formatDate(getValue("[name='project[completed_at]']"))
      dateRows = `${this._row("Start", startDate)}${this._row("End", endDate)}`
    }

    // Bidding-specific review sections
    let biddingReview = ""
    if (isBidding) {
      const preBidSchedule = getRaw("[name='project[pre_bid_meeting_scheduled]']")
      const bidSchedule = getRaw("[name='project[schedule_bid_meeting]']")
      const bidOpeningDateRaw = formatDate(getValue("[name='project[bid_opening_date]']"))
      const bidOpeningHour = getValue("[name='project[bid_opening_hour]']")
      const bidOpeningMin = getValue("[name='project[bid_opening_minutes]']")
      const bidOpeningAmpm = getValue("[name='project[bid_opening_ampm]']")
      const bidOpeningTz = this.hasBidOpeningTzSelectTarget
        ? this.bidOpeningTzSelectTarget.options[this.bidOpeningTzSelectTarget.selectedIndex]?.text || ""
        : ""
      const bidOpeningTime = bidOpeningHour ? `${bidOpeningHour}:${bidOpeningMin || "00"} ${bidOpeningAmpm || "AM"}` : ""
      const bidOpeningDate = [bidOpeningDateRaw, bidOpeningTime, bidOpeningTz].filter(Boolean).join(", ")
      const preBidDate = formatDate(getValue("[name='project[pre_bid_meeting_date]']"))
      const bondRequired = getRaw("[name='project[bond_required]']") === "1" ? "Yes" : "No"
      const unitPricing = getRaw("[name='project[unit_pricing_required]']")
      const alternatesVal = getRaw("[name='project[bid_alternates]']")
      const alternates = { no: "No", yes: "Yes", yes_list: "Yes (List)" }[alternatesVal] || "No"
      const damages = getRaw("[name='project[liquidation_damages_flag]']") === "1" ? "Yes" : "No"

      const preBidLabel = { no: "Not Scheduled", yes: "Virtual", virtual: "Virtual", onsite: "On-site", onsite_virtual: "Virtual & On-site", both: "Virtual & On-site" }[preBidSchedule] || preBidSchedule
      const bidScheduleLabel = bidSchedule === "tbd" ? "Date TBD" : "Scheduled"
      const unitPricingLabel = { no: "No", yes_pdf: "Yes (PDF)", yes_list: "Yes (List)" }[unitPricing] || getValue("[name='project[unit_pricing_required]']")

      const paymentMethodEl = this.element.querySelector("[name='project[payment_method]']")
      let paymentLabel = ""
      if (paymentMethodEl) {
        const pmVal = paymentMethodEl.value
        const pmText = paymentMethodEl.options[paymentMethodEl.selectedIndex]?.text || ""
        paymentLabel = pmText || { new_cc: "New Credit Card", existing_card: "Existing Card", eft: "EFT" }[pmVal] || pmVal
      } else if (this.element.querySelector("[data-payment-plan='unlimited']")) {
        paymentLabel = "Included in Unlimited Plan"
      } else {
        paymentLabel = "Included in plan"
      }

      biddingReview = `
        <div class="mt-6 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <h4 class="font-semibold text-text-muted uppercase tracking-wide mb-3" style="font-size: 13px">Bidding Details</h4>
            <div class="text-sm">
              ${this._row("Pre-Bid Meeting", preBidLabel)}
              ${preBidSchedule !== "no" ? this._row("Pre-Bid Date", preBidDate) : ""}
              ${this._row("Bid Opening", bidScheduleLabel)}
              ${bidSchedule !== "tbd" ? this._row("Bid Opening Date", bidOpeningDate) : ""}
            </div>
          </div>
          <div>
            <h4 class="font-semibold text-text-muted uppercase tracking-wide mb-3" style="font-size: 13px">Bid Settings</h4>
            <div class="text-sm">
              ${this._row("Bid Bond", bondRequired)}
              ${this._row("Unit Pricing", unitPricingLabel)}
              ${this._row("Bid Alternates", alternates)}
              ${this._row("Liquidated Damages", damages)}
              ${this._row("Payment", paymentLabel)}
            </div>
          </div>
        </div>
      `
    }

    // Collect team cards
    const teamCards = this.element.querySelectorAll("[data-controller='team-card']")
    let teamHtml = ""
    const roleColor = (role) => {
      switch (role) {
        case "contractors":  return "#4A90E2"
        case "consultants":  return "#9B59B6"
        case "support":      return "#6B7280"
        case "owner_reps":   return "#16A34A"
        default:             return "#E67E22"
      }
    }

    // Owner company section — always appears first
    const ownerEl = this.element.querySelector("[data-controller*='owner-members']")
    if (ownerEl) {
      const ownerCompanyName = ownerEl.dataset.ownerMembersCompanyNameValue || ""
      const ownerChips = ownerEl.querySelectorAll("[data-chip-email]")
      const ownerColor = "#16a34a"
      const ownerMemberChips = Array.from(ownerChips).map(c => {
        const name = c.textContent.trim().replace(/×$/, "").trim()
        if (!name) return ""
        return `<span class="inline-flex items-center px-2 py-0.5 rounded-full font-medium" style="font-size:13px;color:${ownerColor};background:${ownerColor}12;border:1px solid ${ownerColor}4D;">${this._esc(name)}</span>`
      }).filter(Boolean).join("")
      if (ownerCompanyName) {
        teamHtml += `
          <div class="flex items-start gap-3 py-2">
            <div class="min-w-0">
              <div class="text-sm font-medium text-gray-900">${this._esc(ownerCompanyName)}</div>
              <div class="text-text-muted" style="font-size: 13px">Owner</div>
              ${ownerMemberChips ? `<div class="flex flex-wrap gap-1 mt-1.5">${ownerMemberChips}</div>` : ""}
            </div>
          </div>`
      }
    }
    teamCards.forEach(card => {
      const section = card.closest("[data-team-section]")?.dataset.teamSection || ""
      const companyInput = card.querySelector("[data-team-card-target='companyInput']")
      const companyName = companyInput?.tagName === "SELECT"
        ? (companyInput.options[companyInput.selectedIndex]?.text || "")
        : (companyInput?.value || "")
      if (!companyName) return
      const color = roleColor(card.dataset.teamCardRoleValue)
      const chips = card.querySelectorAll("[data-team-card-target='chip']")
      const memberChips = Array.from(chips).map(c => {
        const name = c.textContent.trim().replace(/×$/, "").trim()
        return `<span class="inline-flex items-center px-2 py-0.5 rounded-full font-medium" style="font-size:13px;color:${color};background:${color}12;border:1px solid ${color}4D;">${this._esc(name)}</span>`
      }).join("")
      teamHtml += `
        <div class="flex items-start gap-3 py-2">
          <div class="min-w-0">
            <div class="text-sm font-medium text-gray-900">${this._esc(companyName)}</div>
            <div class="text-text-muted" style="font-size: 13px">${this._esc(section)}</div>
            ${memberChips ? `<div class="flex flex-wrap gap-1 mt-1.5">${memberChips}</div>` : ""}
          </div>
        </div>`
    })

    const cityStateZip = [city, state, zipcode].filter(Boolean).join(", ")
    const addressHtml = address ? `<div class="flex justify-between py-1.5 border-b border-gray-100"><span class="text-text-muted shrink-0 pr-3">Address</span><span class="text-gray-900 text-right">${this._esc(address)}${cityStateZip ? `<br>${this._esc(cityStateZip)}` : ""}</span></div>` : ""

    const teamLabel = isBidding ? "Bidding Team" : "Team"

    this.reviewContentTarget.innerHTML = `
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <h4 class="font-semibold text-text-muted uppercase tracking-wide mb-3" style="font-size: 13px">Phase & Location</h4>
          <div class="text-sm">
            ${this._row("Phase", phase)}
            ${this._row("Property", tagDisplay)}
            ${addressHtml}
            ${timezone && !isBidding && this.phaseValue !== "dev" ? this._row("Timezone", timezone) : ""}
          </div>
        </div>
        <div>
          <h4 class="font-semibold text-text-muted uppercase tracking-wide mb-3" style="font-size: 13px">Project Details</h4>
          <div class="text-sm">
            ${this._row("Category", portfolio)}
            ${this._row("Subcategory", program)}
            ${this._row("Description", kind)}
            ${this._row("Area", sqm)}
            ${this._row("Number", number)}
            ${dateRows}
          </div>
        </div>
      </div>
      ${biddingReview}
      ${teamHtml ? `
        <div class="mt-6 pt-4 border-t border-gray-200">
          <h4 class="font-semibold text-text-muted uppercase tracking-wide mb-3" style="font-size: 13px">${teamLabel}</h4>
          <div class="divide-y divide-gray-100">${teamHtml}</div>
        </div>
      ` : ""}
    `
  }

  _buildRealEstateReview() {
    const getValue = (selector) => {
      const el = this.element.querySelector(selector)
      if (!el) return ""
      if (el.tagName === "SELECT") {
        const val = el.value
        if (!val) return ""
        const text = el.options[el.selectedIndex]?.text || ""
        return text.match(/^select/i) ? "" : text
      }
      return el.value || el.textContent || ""
    }

    const propertyNum = getValue("[name='project[property_num]']")
    const entity = getValue("[name='project[entity]']")
    const propertyType = getValue("[name='project[property_type]']")
    const region = getValue("[name='project[region_id]']")
    const workOrderType = getValue("[name='project[work_order_type]']")
    const workOrderNum = getValue("[name='project[work_order_num]']")
    const workOrderReceived = getValue("[name='project[work_order_received]']")
    const stake = getValue("[name='project[stake_mission]']")
    const customerRequest = getValue("[name='project[customer_request]']")

    // Collect RE role emails
    const reRoles = [
      { label: "RE PM (Owner)", name: "real_estate_project_manager_owner" },
      { label: "RE Analyst", name: "real_estate_analyst" },
      { label: "RE PM", name: "real_estate_project_manager" },
      { label: "Area PM", name: "area_project_manager" },
      { label: "Facilities Manager", name: "facilities_manager" },
      { label: "Project Manager", name: "project_manager" },
      { label: "PM Clerk (AA)", name: "project_manager_clerk" },
      { label: "Stake President", name: "stake_president" },
      { label: "NRS", name: "nrs" },
      { label: "Closing", name: "closing" }
    ]

    let teamHtml = ""
    reRoles.forEach(role => {
      const email = getValue(`[name='${role.name}']`)
      if (email) {
        teamHtml += this._row(role.label, email)
      }
    })

    // Collect team cards (outsource partners & consultants)
    let cardTeamHtml = ""
    const reTeamSection = this.hasRealEstateTeamSectionTarget ? this.realEstateTeamSectionTarget : null
    if (reTeamSection) {
      reTeamSection.querySelectorAll("[data-controller='team-card']").forEach(card => {
        const section = card.closest("[data-team-section]")?.dataset.teamSection || ""
        const companyInput = card.querySelector("[data-team-card-target='companyInput']")
        const companyName = companyInput?.value || ""
        if (!companyName) return
        const chips = card.querySelectorAll("[data-team-card-target='chip']")
        const memberNames = Array.from(chips).map(c => {
          return `<span class="inline-flex items-center px-2 py-0.5 rounded-full font-medium" style="font-size:13px;color:#6B7280;background:#6B728012;border:1px solid #6B72804D;">${this._esc(c.textContent.trim().replace(/×$/, "").trim())}</span>`
        }).join("")
        cardTeamHtml += `
          <div class="flex items-start gap-3 py-2">
            <div class="min-w-0">
              <div class="text-sm font-medium text-gray-900">${this._esc(companyName)}</div>
              <div class="text-text-muted" style="font-size: 13px">${this._esc(section)}</div>
              ${memberNames ? `<div class="flex flex-wrap gap-1 mt-1.5">${memberNames}</div>` : ""}
            </div>
          </div>`
      })
    }

    this.reviewContentTarget.innerHTML = `
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <h4 class="font-semibold text-text-muted uppercase tracking-wide mb-3" style="font-size: 13px">Phase & Location</h4>
          <div class="text-sm">
            ${this._row("Phase", "Real Estate")}
            ${this._row("Property #", propertyNum)}
            ${this._row("Entity", entity)}
            ${this._row("Property Type", propertyType)}
            ${this._row("Region", region)}
          </div>
        </div>
        <div>
          <h4 class="font-semibold text-text-muted uppercase tracking-wide mb-3" style="font-size: 13px">Project Details</h4>
          <div class="text-sm">
            ${this._row("Work Order Type", workOrderType)}
            ${this._row("Work Order #", workOrderNum)}
            ${this._row("Work Order Received", workOrderReceived)}
            ${this._row("Stake", stake)}
            ${this._row("Customer Request", customerRequest)}
          </div>
        </div>
      </div>
      ${teamHtml ? `
        <div class="mt-6 pt-4 border-t border-gray-200">
          <h4 class="font-semibold text-text-muted uppercase tracking-wide mb-3" style="font-size: 13px">Real Estate Team</h4>
          <div class="text-sm">${teamHtml}</div>
        </div>
      ` : ""}
      ${cardTeamHtml ? `
        <div class="mt-6 pt-4 border-t border-gray-200">
          <h4 class="font-semibold text-text-muted uppercase tracking-wide mb-3" style="font-size: 13px">Partners & Consultants</h4>
          <div class="divide-y divide-gray-100">${cardTeamHtml}</div>
        </div>
      ` : ""}
    `
  }

  _row(label, value) {
    return value ? `<div class="flex justify-between py-1.5 px-2 -mx-2 rounded border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"><span class="text-text-muted shrink-0 pr-3">${label}</span><span class="text-gray-900 text-right">${this._esc(value)}</span></div>` : ""
  }

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }
}
